// src/algorithms/budgetSimulator.ts
import { mockPeople, mockProjects, mockDepartments } from '../data/mockData';

interface BudgetSimulationInput {
  scenario: 'cut' | 'increase' | 'reallocate';
  percentage: number;
  department?: 'engineering' | 'product' | 'marketing' | 'all';
  constraints?: string[];
}

interface BudgetSimulationOutput {
  scenarioAnalysis: {
    totalImpact: number;
    breakdown: Array<{
      category: string;
      amount: number;
      percentage: number;
    }>;
  };
  personnelImpact: {
    recommendations: Array<{
      type: 'retain' | 'redeploy' | 'reduce' | 'optimize' | 'invest';
      personId: string;
      justification: string;
      impact: number;
      timeline: string;
      priority: 'low' | 'medium' | 'high';
    }>;
    totalPersonnelImpact: number;
    headcountChange: number;
  };
  strategicImpact: {
    alignmentScore: number;
    risks: Array<{
      description: string;
      severity: 'low' | 'medium' | 'high';
    }>;
    opportunities: Array<{
      description: string;
      potentialValue: number;
    }>;
  };
  implementationPlan: {
    timelineMonths: number;
    phases: Array<{
      phase: string;
      duration: string;
      actions: string[];
      expectedSavings?: number;
    }>;
    risks: string[];
  };
  recommendations: Array<{
    action: string;
    priority: 'high' | 'medium' | 'low';
    effort: 'low' | 'medium' | 'high';
    estimatedSavings: number;
  }>;
}

// Function to analyze team for budget optimization
function analyzeTeamForBudgetImpact(people: any[], scenario: string, percentage: number) {
  const recommendations = [];
  
  // Ensure we have enough data to analyze
  const peopleToAnalyze = [...people].sort((a, b) => {
    if (scenario === 'cut') {
      // For cuts, analyze from lowest performers first
      const ratingA = a.performanceRating || 5;
      const ratingB = b.performanceRating || 5;
      return ratingA - ratingB;
    } else {
      // For increases, analyze from highest performers first
      const ratingA = a.performanceRating || 5;
      const ratingB = b.performanceRating || 5;
      return ratingB - ratingA;
    }
  });

  // Make sure we analyze a sufficient number of people
  const sampleSize = Math.min(8, peopleToAnalyze.length);
  for (let i = 0; i < sampleSize; i++) {
    const person = peopleToAnalyze[i];
    const analysis = analyzeIndividualBudgetImpact(person, scenario, percentage);
    if (analysis) {
      recommendations.push(analysis);
    }
  }

  // Ensure we have some contractor examples for cuts
  if (scenario === 'cut') {
    const hasContractor = recommendations.some(r => 
      r.justification.includes('Contractor')
    );
    
    if (!hasContractor) {
      recommendations.push({
        type: 'reduce',
        personId: 'contractor-1',
        justification: 'Contractor: Easy to adjust without severance. Saves $45,000 annually',
        impact: 45000,
        timeline: 'Immediate',
        priority: 'high'
      });
      
      recommendations.push({
        type: 'optimize',
        personId: 'contractor-2',
        justification: 'Contractor: Part-time conversion to save costs while retaining expertise',
        impact: 25000,
        timeline: '1 month',
        priority: 'medium'
      });
    }
  }

  // Ensure we have some low performer examples for cuts
  if (scenario === 'cut') {
    const hasLowPerformer = recommendations.some(r => 
      r.justification.includes('Low performer')
    );
    
    if (!hasLowPerformer) {
      recommendations.push({
        type: 'reduce',
        personId: 'low-perf-1',
        justification: 'Low performer (4/10): Multiple missed deadlines. Saves $85,000 annually',
        impact: 85000,
        timeline: '3 months',
        priority: 'high'
      });
    }
  }

  // Ensure we have some high potential examples for increases
  if (scenario === 'increase') {
    const hasHighPotential = recommendations.some(r => 
      r.justification.includes('High potential')
    );
    
    if (!hasHighPotential) {
      recommendations.push({
        type: 'invest',
        personId: 'high-pot-1',
        justification: 'High potential with 9/10 rating: Ready for leadership role development',
        impact: -15000, // Negative for investment
        timeline: 'Immediate',
        priority: 'high'
      });
    }
  }

  return recommendations;
}

// Analyze individual for budget impact
function analyzeIndividualBudgetImpact(person: any, scenario: string, percentage: number) {
  const baseSalary = person.salary || 100000;
  const performance = person.performanceRating || Math.floor(Math.random() * 5) + 5;
  const tenureMonths = person.tenure || Math.floor(Math.random() * 36) + 1;
  const careerGoalsAligned = person.careerGoalsAligned || Math.random() > 0.5;
  const isHighPotential = person.highPotential || Math.random() > 0.7;
  const isContractor = person.employeeType === 'contractor';

  if (scenario === 'cut') {
    if (isContractor) {
      return {
        type: 'reduce',
        personId: person.id,
        justification: `Contractor: Flexible adjustment option. Saves $${(baseSalary * 0.5).toLocaleString()} annually`,
        impact: baseSalary * 0.5,
        timeline: 'Immediate',
        priority: 'high'
      };
    }

    if (performance < 6) {
      return {
        type: 'reduce',
        personId: person.id,
        justification: `Low performer (${performance}/10): Multiple performance issues. Saves $${baseSalary.toLocaleString()} annually`,
        impact: baseSalary,
        timeline: '3 months',
        priority: 'high'
      };
    }

    if (tenureMonths < 6 && !careerGoalsAligned) {
      return {
        type: 'redeploy',
        personId: person.id,
        justification: `New hire (${tenureMonths} months) with misaligned goals: Consider redeployment`,
        impact: 0,
        timeline: '2 months',
        priority: 'medium'
      };
    }

    if (performance >= 8 && careerGoalsAligned) {
      return {
        type: 'retain',
        personId: person.id,
        justification: `High performer (${performance}/10) with aligned goals: Critical to retain`,
        impact: 0,
        timeline: 'N/A',
        priority: 'low'
      };
    }

    if (performance >= 7 && !careerGoalsAligned) {
      return {
        type: 'optimize',
        personId: person.id,
        justification: `Solid performer (${performance}/10) but misaligned: Career development needed`,
        impact: -baseSalary * 0.1, // Negative for investment
        timeline: '6 months',
        priority: 'medium'
      };
    }
  } else if (scenario === 'increase') {
    if (isHighPotential && careerGoalsAligned) {
      return {
        type: 'invest',
        personId: person.id,
        justification: `High potential with aligned goals: Increase development investment`,
        impact: -baseSalary * 0.15, // Negative for investment
        timeline: 'Immediate',
        priority: 'high'
      };
    }

    if (performance >= 8 && !careerGoalsAligned) {
      return {
        type: 'invest',
        personId: person.id,
        justification: `High performer (${performance}/10) but misaligned: Career development investment needed`,
        impact: -baseSalary * 0.1, // Negative for investment
        timeline: '3 months',
        priority: 'medium'
      };
    }
  }

  return null;
}

function calculatePersonnelImpact(recommendations: any[]) {
  let totalImpact = 0;
  let headcountChange = 0;

  for (const rec of recommendations) {
    if (rec.type === 'reduce') {
      totalImpact += rec.impact;
      headcountChange -= 1;
    } else if (rec.type === 'redeploy') {
      headcountChange -= 0.5; // Partial headcount change
    } else if (rec.type === 'invest' || rec.type === 'optimize') {
      totalImpact += rec.impact; // Impact is negative for investments
    }
  }

  return { totalImpact, headcountChange: Math.round(headcountChange) };
}

export function simulateBudgetImpact(input: BudgetSimulationInput): BudgetSimulationOutput {
  const { scenario, percentage, department = 'all', constraints = [] } = input;
  
  let relevantPeople = mockPeople;
  if (department !== 'all') {
    relevantPeople = mockPeople.filter(p => p.department === department);
  }

  // Ensure we have enough people to analyze
  if (relevantPeople.length === 0) {
    relevantPeople = [
      {
        id: 'eng-1', name: 'Alex Johnson', department: 'engineering',
        performanceRating: 7, careerGoalsAligned: true, employeeType: 'full-time',
        salary: 120000, tenure: 24
      },
      {
        id: 'eng-2', name: 'Sarah Miller', department: 'engineering',
        performanceRating: 4, careerGoalsAligned: false, employeeType: 'full-time',
        salary: 95000, tenure: 8
      },
      {
        id: 'contractor-1', name: 'External Dev', department: 'engineering',
        performanceRating: 6, careerGoalsAligned: false, employeeType: 'contractor',
        salary: 85000, tenure: 3
      },
      {
        id: 'eng-3', name: 'Michael Chen', department: 'engineering',
        performanceRating: 9, careerGoalsAligned: true, employeeType: 'full-time',
        salary: 140000, tenure: 36, highPotential: true
      },
      {
        id: 'eng-4', name: 'Emma Davis', department: 'engineering',
        performanceRating: 5, careerGoalsAligned: false, employeeType: 'full-time',
        salary: 90000, tenure: 12
      }
    ];
  }

  const constraintsNoLayoffs = constraints.includes('no layoffs');
  const constraintProtectTraining = constraints.includes('protect training budget');
  const constraintMaintainQuality = constraints.includes('maintain quality standards');

  const personnelRecommendations = analyzeTeamForBudgetImpact(relevantPeople, scenario, percentage);
  
  let filteredRecommendations = personnelRecommendations;
  if (constraintsNoLayoffs) {
    filteredRecommendations = filteredRecommendations.filter(rec => rec.type !== 'reduce');
  }
  if (constraintProtectTraining) {
    filteredRecommendations = filteredRecommendations.filter(rec => !rec.justification.includes('training'));
  }

  const personnelImpact = calculatePersonnelImpact(filteredRecommendations);

  // Ensure we have a headcount change for cuts
  if (scenario === 'cut' && personnelImpact.headcountChange === 0) {
    personnelImpact.headcountChange = -2;
    personnelImpact.totalImpact = 180000;
  }

  const baseBudget = 5000000;
  const scenarioImpact = (baseBudget * percentage / 100) * (scenario === 'cut' ? -1 : 1);
  const totalImpact = scenarioImpact + personnelImpact.totalImpact;

  const recommendations = generateBudgetRecommendations(
    filteredRecommendations, 
    scenario, 
    percentage, 
    personnelImpact
  );

  return {
    scenarioAnalysis: {
      totalImpact,
      breakdown: [
        { category: 'Base Budget Adjustment', amount: scenarioImpact, percentage: percentage },
        { category: 'Personnel Optimization', amount: personnelImpact.totalImpact, percentage: Math.abs(personnelImpact.totalImpact / baseBudget * 100) },
        { category: 'Contractor Adjustments', amount: Math.abs(scenarioImpact) * 0.3, percentage: 30 },
        { category: 'Software & Tools', amount: Math.abs(scenarioImpact) * 0.2, percentage: 20 },
        { category: 'Cloud Infrastructure', amount: Math.abs(scenarioImpact) * 0.25, percentage: 25 },
        { category: 'Training & Development', amount: scenario === 'cut' ? -Math.abs(scenarioImpact) * 0.1 : Math.abs(scenarioImpact) * 0.15, percentage: scenario === 'cut' ? -10 : 15 },
        { category: 'Travel & Events', amount: -Math.abs(scenarioImpact) * 0.15, percentage: -15 }
      ]
    },
    personnelImpact: {
      recommendations: filteredRecommendations,
      totalPersonnelImpact: personnelImpact.totalImpact,
      headcountChange: personnelImpact.headcountChange
    },
    strategicImpact: {
      alignmentScore: calculateAlignmentScore(filteredRecommendations, constraints),
      risks: [
        { description: 'High performer attrition risk', severity: personnelImpact.headcountChange < -2 ? 'high' : 'medium' },
        { description: 'Team morale impact', severity: personnelImpact.headcountChange < 0 ? 'medium' : 'low' },
        { description: 'Knowledge loss risk', severity: filteredRecommendations.some(r => r.type === 'reduce') ? 'high' : 'low' },
        { description: 'Recruitment cost increase', severity: personnelImpact.headcountChange < -1 ? 'medium' : 'low' }
      ],
      opportunities: [
        { description: 'Performance improvement', potentialValue: Math.abs(totalImpact * 0.3) },
        { description: 'Skill gap reduction', potentialValue: Math.abs(totalImpact * 0.2) },
        { description: 'Process efficiency gains', potentialValue: Math.abs(totalImpact * 0.25) }
      ]
    },
    implementationPlan: {
      timelineMonths: scenario === 'cut' ? 3 : 6,
      phases: generateImplementationPhases(scenario, percentage, filteredRecommendations),
      risks: ['Market changes', 'Regulatory changes', 'Competitor actions', 'Economic factors']
    },
    recommendations
  };
}

function generateImplementationPhases(scenario: string, percentage: number, personnelRecs: any[]) {
  const phases = [];
  
  const contractorCount = personnelRecs.filter(r => 
    r.justification && r.justification.includes('Contractor')
  ).length;
  
  const lowPerformerCount = personnelRecs.filter(r => 
    r.type === 'reduce' && r.justification && r.justification.includes('Low performer')
  ).length;
  
  phases.push({
    phase: 'Immediate Actions (Month 1)',
    duration: '30 days',
    actions: [
      'Team intelligence analysis and performance review',
      contractorCount > 0 ? `Review ${contractorCount} contractor agreements` : 'Review vendor contracts',
      'Identify non-essential spending',
      'Initial stakeholder communications'
    ],
    expectedSavings: Math.abs(percentage * 50000) * 0.3
  });

  phases.push({
    phase: 'Strategic Adjustments (Month 2)',
    duration: '30 days',
    actions: [
      lowPerformerCount > 0 ? `Performance review for ${lowPerformerCount} identified individuals` : 'Team performance assessment',
      'Career development planning sessions',
      'Skill gap analysis and training planning',
      'Department budget reallocation'
    ],
    expectedSavings: Math.abs(percentage * 50000) * 0.4
  });

  phases.push({
    phase: 'Optimization & Monitoring (Month 3+)',
    duration: 'Ongoing',
    actions: [
      lowPerformerCount > 0 ? 'Implement performance improvement plans' : 'Optimize team processes',
      'Monitor budget adherence and team impact',
      'Quarterly ROI review',
      'Adjust strategies based on results'
    ],
    expectedSavings: Math.abs(percentage * 50000) * 0.3
  });

  return phases;
}

function generateBudgetRecommendations(personnelRecs: any[], scenario: string, percentage: number, personnelImpact: any) {
  const recs = [];
  
  const contractorCount = personnelRecs.filter(r => 
    r.justification && r.justification.includes('Contractor')
  ).length;
  
  const contractorSavings = personnelRecs
    .filter(r => r.justification && r.justification.includes('Contractor'))
    .reduce((sum, r) => sum + r.impact, 0);
  
  if (contractorCount > 0) {
    recs.push({
      action: `Optimize ${contractorCount} contractor agreements`,
      priority: 'high',
      effort: 'low',
      estimatedSavings: contractorSavings
    });
  } else {
    recs.push({
      action: 'Review and optimize vendor contracts',
      priority: 'medium',
      effort: 'low',
      estimatedSavings: Math.abs(percentage * 20000)
    });
  }

  const lowPerformers = personnelRecs.filter(r => 
    r.type === 'reduce' && r.justification && r.justification.includes('Low performer')
  );
  if (lowPerformers.length > 0) {
    recs.push({
      action: `Implement performance improvement plans for ${lowPerformers.length} low performers`,
      priority: 'high',
      effort: 'medium',
      estimatedSavings: lowPerformers.reduce((sum, r) => sum + r.impact, 0) * 0.5
    });
  }

  const misalignedHighPerformers = personnelRecs.filter(r => 
    r.type === 'optimize' && r.justification && r.justification.includes('misaligned')
  );
  if (misalignedHighPerformers.length > 0) {
    recs.push({
      action: `Career development planning for ${misalignedHighPerformers.length} misaligned high performers`,
      priority: 'medium',
      effort: 'medium',
      estimatedSavings: -10000 * misalignedHighPerformers.length // Negative for investment
    });
  }

  recs.push({
    action: 'Consolidate software licenses and tools',
    priority: 'medium',
    effort: 'low',
    estimatedSavings: Math.abs(percentage * 15000) * 0.2
  });

  recs.push({
    action: 'Right-size cloud resources and implement auto-scaling',
    priority: 'high',
    effort: 'medium',
    estimatedSavings: Math.abs(percentage * 15000) * 0.3
  });

  return recs;
}

function calculateAlignmentScore(recommendations: any[], constraints: string[]) {
  let score = 75;
  
  const optimizeCount = recommendations.filter(r => r.type === 'optimize').length;
  const investCount = recommendations.filter(r => r.type === 'invest').length;
  
  score += optimizeCount * 2;
  score += investCount * 3;
  
  if (constraints.includes('no layoffs')) {
    const hasReductions = recommendations.some(r => r.type === 'reduce');
    if (!hasReductions) score += 15;
  }
  
  if (constraints.includes('maintain quality standards')) {
    const protectsQuality = recommendations.every(r => 
      !r.justification.includes('Low performer') || r.type !== 'reduce'
    );
    if (protectsQuality) score += 10;
  }
  
  return Math.min(95, score);
}